<template>
  <div class="import-dup">
    <b-form-checkbox v-model="enabled" switch :disabled="busy">
      {{ $t("import_teamsv2.dup_check", "Check for teams that already exist") }}
    </b-form-checkbox>
    <p class="import-dup__hint">
      {{
        $t(
          "import_teamsv2.dup_check_hint",
          "Compare the file with the campaign's teams before importing, and choose what happens to the rows that match."
        )
      }}
    </p>

    <template v-if="checkDuplicates">
      <p class="import-dup__label">
        {{ $t("import_teamsv2.dup_fields_label", "A row is a duplicate when all of these match") }}
      </p>
      <b-form-checkbox-group v-model="fields" :options="fieldOptions" :disabled="busy" />
      <p class="import-dup__hint">
        {{
          $t(
            "import_teamsv2.dup_fields_hint",
            "Only fields mapped to a column or a fixed value can be compared. Names and emails ignore upper and lower case, phones compare digits only."
          )
        }}
      </p>

      <template v-if="duplicates">
        <p v-if="!duplicates.length" class="import-dup__none">
          {{ $t("import_teamsv2.dup_none", "No duplicate teams found") }}
        </p>

        <div v-else class="import-dup__found">
          <p class="import-dup__found-head">
            {{
              $t(
                "import_teamsv2.dup_found",
                "{count} rows match teams that already exist or repeat an earlier row",
                { count: duplicates.length }
              )
            }}
          </p>

          <ul class="import-dup__list">
            <li v-for="duplicate in listedDuplicates" :key="duplicate.line">
              <strong>{{
                $t("import_teamsv2.dup_line", "Line {line}", { line: duplicate.line })
              }}</strong>
              · {{ duplicate.name || "—" }}
              <div
                v-for="team in duplicate.existing_teams"
                :key="team.id"
                class="import-dup__match"
              >
                {{
                  $t(
                    "import_teamsv2.dup_matches",
                    "Matches the team {name} (shortlink {shortlink}, ID {id})",
                    { name: team.name, shortlink: team.shortlink, id: team.id }
                  )
                }}
              </div>
              <div v-if="duplicate.same_as_line" class="import-dup__match">
                {{
                  $t("import_teamsv2.dup_repeats", "Repeats line {line} of the file", {
                    line: duplicate.same_as_line,
                  })
                }}
              </div>
            </li>
            <li v-if="unlistedCount > 0" class="import-dup__match">
              {{ $t("import_teamsv2.dup_more", "and {count} more", { count: unlistedCount }) }}
            </li>
          </ul>

          <p class="import-dup__label">
            {{ $t("import_teamsv2.dup_action_label", "What should happen to these rows?") }}
          </p>
          <b-form-radio-group v-model="action" stacked :disabled="busy">
            <b-form-radio v-for="option in actionOptions" :key="option.value" :value="option.value">
              {{ option.text }}
              <span class="import-dup__hint d-block">{{ option.hint }}</span>
            </b-form-radio>
          </b-form-radio-group>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
// Most rows listed at once; a larger file says how many more there are.
const MAX_LISTED_DUPLICATES = 50;

// Duplicate check of the team import: whether to look for teams the campaign
// already has, which fields must match, and, once a dry run found duplicates,
// the list and the choice of what to do with them. The parent owns the state
// and binds it with .sync.
export default {
  name: "campaign-import-teams-duplicates",

  props: {
    checkDuplicates: { type: Boolean, default: false },
    matchFields: { type: Array, default: () => [] },
    duplicateAction: { type: String, default: "" },
    // Field keys mapped to a column or a fixed value; only those can be compared.
    mappedFieldKeys: { type: Array, default: () => [] },
    // Duplicates found by the last dry run, null before one ran.
    duplicates: { type: Array, default: null },
    busy: { type: Boolean, default: false },
  },

  computed: {
    enabled: {
      get() {
        return this.checkDuplicates;
      },
      set(value) {
        this.$emit("update:checkDuplicates", value);
      },
    },

    fields: {
      get() {
        return this.matchFields;
      },
      set(value) {
        this.$emit("update:matchFields", value);
      },
    },

    action: {
      get() {
        return this.duplicateAction;
      },
      set(value) {
        this.$emit("update:duplicateAction", value);
      },
    },

    // Shortlinks are always generated by this import, so they are never compared.
    fieldOptions() {
      return [
        { value: "name", text: this.$t("import_teamsv2.dup_field_name", "Team name") },
        {
          value: "leader_email",
          text: this.$t("import_teamsv2.dup_field_email", "Team leader email"),
        },
        { value: "phone", text: this.$t("import_teamsv2.dup_field_phone", "Phone") },
        {
          value: "external_id",
          text: this.$t("import_teamsv2.dup_field_external_id", "External ID"),
        },
      ].map((option) => ({
        ...option,
        disabled: !this.mappedFieldKeys.includes(option.value),
      }));
    },

    actionOptions() {
      return [
        {
          value: "update",
          text: this.$t("import_teamsv2.dup_action_update", "Update the existing teams"),
          hint: this.$t(
            "import_teamsv2.dup_action_update_hint",
            "Each row updates the team it matches. The team keeps its shortlink and every value the row leaves empty."
          ),
        },
        {
          value: "skip",
          text: this.$t("import_teamsv2.dup_action_skip", "Skip the duplicate rows"),
          hint: this.$t(
            "import_teamsv2.dup_action_skip_hint",
            "Rows matching an existing team are left out, and so is every repeat of an earlier row."
          ),
        },
        {
          value: "create",
          text: this.$t("import_teamsv2.dup_action_create", "Import them anyway"),
          hint: this.$t(
            "import_teamsv2.dup_action_create_hint",
            "Every row is created as a new team."
          ),
        },
      ];
    },

    listedDuplicates() {
      return (this.duplicates || []).slice(0, MAX_LISTED_DUPLICATES);
    },

    unlistedCount() {
      return (this.duplicates || []).length - this.listedDuplicates.length;
    },
  },
};
</script>

<style scoped lang="scss">
.import-dup {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e1e6ea;
}

.import-dup__hint {
  margin: 4px 0 0;
  font-size: 13px;
  color: #7f8b98;
}

.import-dup__label {
  margin: 12px 0 6px;
  font-weight: 600;
}

.import-dup__none {
  margin-top: 12px;
  color: #1aa779;
  font-weight: 600;
}

.import-dup__found {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #f0d58c;
  border-radius: 8px;
  background: #fff9e6;
}

.import-dup__found-head {
  margin-bottom: 8px;
  font-weight: 600;
}

.import-dup__list {
  max-height: 220px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  font-size: 14px;

  li {
    padding: 6px 0;
    border-bottom: 1px solid #f0e6c8;
  }
}

.import-dup__match {
  color: #7f8b98;
  font-size: 13px;
}
</style>
